// Store polish stage 2F (his shop notes of 2026-10-29, the store-upkeep action items), loaded on every page by
// layout/theme.liquid. Three enhancements, each a no-op on a page without its mark; the page as rendered stands without it.
//
//  28  "Same joke, other goods" (sections/ap-same-joke): the row's tiles come from the store's own search for the joke's
//      words, rendered by the same section; a tile is kept only when its docket-joke key equals this product's.
//  36  "Browse every gift" (sections/main-collection's ap_browse_label, the gifts page): the row's label names the gift
//      finder's budget, and opening it shows the grid at that price (Shopify's own price filter, in the address).
//  38  Compact colour choice on a phone (the product page's variant picker): a colour option of six or more buttons
//      becomes two rows of labelled buttons with the chosen colour written beside the option's name; the CSS is in
//      assets/ap-polish-f.css and applies below 750 px only.
// No HTML string is written (every node is built or imported), and every address stays on this origin.

const sameOrigin = href => {
  const u = new URL(href, location.origin);
  if (u.origin !== location.origin) throw new Error('ap-polish-f: not this store');
  return u;
};

// ------------------------------------------------------------------------------------------------ 28 · same joke

/**
 * The tiles to show: those whose design key equals `key`, not `handle`, each product once; other product types first, one
 * of each type before a second of any type, then the product's own type; at most `limit`.
 * @param {{joke: string, handle: string, type: string}[]} found  in the search's order
 */
export function pickSameJoke(found, key, handle, type, limit) {
  const seen = new Set([handle]);
  const mine = [];
  const firsts = [];
  const rest = [];
  const types = new Set();
  for (const f of found) {
    if (f.joke !== key || seen.has(f.handle)) continue;
    seen.add(f.handle);
    if (f.type === type) mine.push(f);
    else if (types.has(f.type)) rest.push(f);
    else {
      types.add(f.type);
      firsts.push(f);
    }
  }
  return [...firsts, ...rest, ...mine].slice(0, limit);
}

async function fillJokeRow(row) {
  const list = row.querySelector('[data-ap-joke-list]');
  const { key, q, handle, type } = row.dataset;
  const limit = parseInt(row.dataset.limit, 10) || 8;
  if (!list || !key || !q) return;
  const u = sameOrigin(row.dataset.search || '/search');
  u.searchParams.set('q', q);
  u.searchParams.set('type', 'product');
  u.searchParams.set('options[unavailable_products]', 'hide');
  u.searchParams.set('section_id', 'ap-same-joke');
  const r = await fetch(u, { credentials: 'same-origin' });
  if (!r.ok) return;
  const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
  const found = [...doc.querySelectorAll('[data-ap-joke-item]')].map(node => ({
    joke: node.dataset.joke || '',
    handle: node.dataset.handle || '',
    type: node.dataset.type || '',
    node,
  }));
  const keep = pickSameJoke(found, key, handle, type, limit);
  const tiles = keep.map(f => f.node.querySelector('li')).filter(Boolean).map(li => document.importNode(li, true));
  if (!tiles.length) return;
  list.replaceChildren(...tiles);
  row.hidden = false;
}

// ------------------------------------------------------------------------------------------------ 36 · browse every gift

/** The price filter for a gift-finder budget key ("0-25", "25-50", "100-"), in dollars, as Shopify's filter params. */
export function budgetParams(budget) {
  const m = /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)?$/.exec(budget || '');
  if (!m) return null;
  const out = {};
  if (parseFloat(m[1]) > 0) out['filter.v.price.gte'] = m[1];
  if (m[2] !== undefined) out['filter.v.price.lte'] = m[2];
  return out;
}

/** The address of the grid at `budget`: the page's own address with its price filter set to the budget, page reset. */
export function browseUrl(href, budget) {
  const u = new URL(href);
  const want = budgetParams(budget);
  u.searchParams.delete('filter.v.price.gte');
  u.searchParams.delete('filter.v.price.lte');
  u.searchParams.delete('page');
  if (want) Object.entries(want).forEach(([k, v]) => u.searchParams.set(k, v));
  u.hash = 'ApBrowse';
  return u;
}

function wireBrowse(browse) {
  const form = document.querySelector('[data-apg-form]');
  const label = browse.querySelector('[data-ap-browse-label]');
  if (!form || !label) return;
  const base = label.dataset.base || label.textContent;
  // the finder's chosen budget; before the engine has restored its chips from the address, the address's own
  let settled = false; // after the shopper's first change, the form alone decides
  const budget = () => new FormData(form).get('budget') || (settled ? '' : new URL(location.href).searchParams.get('budget') || '');
  const chip = v => {
    const input = v && form.querySelector('input[name="budget"][value="' + CSS.escape(v) + '"]');
    const face = input && input.parentElement.querySelector('.apg-chip__face');
    return face ? face.textContent.trim() : '';
  };
  const matches = () => {
    const here = new URL(location.href);
    const want = budgetParams(budget()) || {};
    return ['filter.v.price.gte', 'filter.v.price.lte'].every(k => (here.searchParams.get(k) || '') === (want[k] || ''));
  };
  const sync = () => {
    const name = chip(budget());
    label.textContent = name ? base + ' · ' + name : base;
  };
  form.addEventListener('change', event => {
    if (event.target.name !== 'budget') return;
    settled = true;
    sync();
    if (browse.open && !matches()) browse.open = false; // the grid no longer shows the chosen budget: fold it until reopened
  });
  browse.addEventListener('toggle', () => {
    if (browse.open && budget() && !matches()) location.assign(sameOrigin(browseUrl(location.href, budget()).href).href);
  });
  sync();
}

// ------------------------------------------------------------------------------------------------ 38 · compact colours

const COLOUR = /^\s*colou?r\s*$/i;

/**
 * The labels without the last word most of them share ("Black Accent" … "Red Accent", "Dark Green" → "Black" … "Red",
 * "Dark Green"): a word that ends at least two-thirds of the names, cut only where it ends a name of two words or more.
 * With no such word, the names as given. The full name stays in the input's own label and in the chosen line.
 */
export function shortLabels(values) {
  const words = values.map(v => v.trim().split(/\s+/));
  const counts = new Map();
  words.forEach(w => w.length > 1 && counts.set(w[w.length - 1], (counts.get(w[w.length - 1]) || 0) + 1));
  const [last, n] = [...counts.entries()].sort((x, y) => y[1] - x[1])[0] || ['', 0];
  if (!last || n < 2 || n * 3 < values.length * 2) return values.slice();
  return words.map((w, i) => (w.length > 1 && w[w.length - 1] === last ? w.slice(0, -1).join(' ') : values[i]));
}

function compactColours(root) {
  root.querySelectorAll('fieldset.variant-option--buttons:not(.variant-option--swatches)').forEach(fs => {
    const legend = fs.querySelector(':scope > legend');
    const name = legend ? (legend.firstChild && legend.firstChild.nodeType === 3 ? legend.firstChild.textContent : legend.textContent) : '';
    const inputs = [...fs.querySelectorAll('input[type="radio"]')];
    if (!legend || !COLOUR.test(name.replace(/:.*$/, '')) || inputs.length < 6) return;
    fs.classList.add('ap-colour-compact');
    fs.style.setProperty('--ap-cols', String(Math.min(6, Math.ceil(inputs.length / 2))));
    const texts = inputs.map(i => i.closest('label').querySelector('.variant-option__button-label__text'));
    const shorts = shortLabels(inputs.map(i => i.value));
    texts.forEach((t, n) => t && t.dataset.apShort !== shorts[n] && (t.dataset.apShort = shorts[n]));
    const picked = inputs.find(i => i.checked);
    const head = legend.firstChild;
    if (head && head.nodeType === 3 && head.textContent !== head.textContent.trim()) head.textContent = head.textContent.trim();
    let chosen = legend.querySelector('.ap-chosen');
    if (!chosen) {
      chosen = document.createElement('span');
      chosen.className = 'ap-chosen';
      legend.append(chosen);
    }
    const want = picked ? ': ' + picked.value : '';
    if (chosen.textContent !== want) chosen.textContent = want;
  });
}

// ------------------------------------------------------------------------------------------------ boot

if (typeof document !== 'undefined') {
  let observing = false;
  const boot = () => {
    // idempotent: boot runs as soon as the document is parsed and again at load, so a row the page adds late is filled
    document.querySelectorAll('[data-ap-joke-row]:not([data-ap-done])').forEach(row => {
      row.dataset.apDone = '';
      fillJokeRow(row).catch(() => delete row.dataset.apDone);
    });
    document.querySelectorAll('[data-ap-browse]:not([data-ap-done])').forEach(browse => {
      browse.dataset.apDone = '';
      wireBrowse(browse);
    });
    if (!observing && document.querySelector('variant-picker')) {
      observing = true;
      compactColours(document);
      // the picker is re-rendered on every variant change (Horizon morphs the section): mark it again after each
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          compactColours(document);
        });
      }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'data-current-checked'] });
      document.addEventListener('change', event => {
        if (event.target.closest && event.target.closest('variant-picker')) compactColours(document);
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  if (document.readyState !== 'complete') window.addEventListener('load', boot, { once: true });
}
